import {
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  athleteDisplayName,
  AthleteTeamProfile,
  WeightUnit,
  zWeightUnit,
} from '@strength/domain';
import { getMetricSpec } from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import {
  createColumnHelper,
  flexRender,
  type Row,
} from '@tanstack/react-table';
import { MdEditNote, MdNotes, MdSearch, MdSettings } from '@teamworksdev/icons';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AthleteAvatar } from '../../../components/icons/athlete-avatar';
import { DisabledWithTooltip } from '../../../components/permissions/DisabledWithTooltip';
import {
  DataTableProvider,
  useDataTableContext,
} from '../../../components/table/data-table-context';
import {
  DefaultEmptySearchResultsPlaceholder,
  DefaultErrorPlaceholder,
  DefaultNoDataPlaceholder,
} from '../../../components/table/data-table-placeholder';
import { DataTableRow } from '../../../components/table/data-table-row';
import { PaginationFooter } from '../../../components/table/pagination';
import { cn } from '../../../components/utils';
import { useToast } from '../../../hooks/useToast';
import { CoreExercise } from '../utils';
import { EditCellInput } from './EditCellInput';

export type AthleteExerciseBenchmarkRow = {
  id: string;
  index: number;
  profile: string;
  athlete: AthleteTeamProfile;
} & { [key: string]: ExerciseBenchmarkValue };

export type AthleteExerciseBenchmarkCell = {
  profileId: string;
  libraryExerciseId: string;
  benchmarkGridId: string;
  value: number;
  unit: WeightUnit;
};

export type ExerciseBenchmarkValue = {
  id: string;
  value: number | null;
  unit: WeightUnit | null;
};

const handleValueChange = (
  value: string,
  profileId: string,
  libraryExerciseId: string,
  benchmarkGridId: string,
  onSaveBenchmarkValueCell: (
    cell: AthleteExerciseBenchmarkCell,
    onSuccess: () => void,
  ) => void,
  toast: ReturnType<typeof useToast>['toast'],
  focusedIndex: number | null,
  setFocusedIndex: (index: number | null) => void,
  t: I18nTranslator,
): Error | void => {
  const trimmedValue = value.replace(/\s/g, '');
  if (trimmedValue === '') {
    toast({
      title: t('invalidValue'),
      description: t('cannotRemoveBenchmarkValue'),
      severity: 'error',
    });
    return new Error('Cannot remove value');
  }

  const result = getMetricSpec({ type: 'intensity' }).schema.safeParse(
    trimmedValue,
  );

  if (
    !result.success ||
    result.data?.format !== 'weight' ||
    result.data?.type !== 'exact'
  ) {
    toast({
      title: t('invalidValue'),
      description: t('invalidBenchmarkUnits', {
        units: zWeightUnit.options.map((unit) => `"X${unit}"`).join(', '),
      }),
      severity: 'error',
    });
    return new Error('Invalid Value');
  }

  onSaveBenchmarkValueCell(
    {
      profileId,
      libraryExerciseId,
      benchmarkGridId,
      value: result.data.value,
      unit: result.data.unit,
    },
    () => {
      // Set focus after re-render
      if (focusedIndex !== null) {
        requestAnimationFrame(() => {
          const inputs = Array.from(
            document.querySelectorAll('input[type="text"][data-edit-input]'),
          ) as HTMLInputElement[];
          inputs[focusedIndex]?.focus();
          inputs[focusedIndex]?.select();
          setFocusedIndex(null);
        });
      }
    },
  );
};

type BenchmarksDataTableProps = {
  data: AthleteExerciseBenchmarkRow[];
  benchmarkGridId: string;
  coreExercises: CoreExercise[];
  onSetAthleteId: (profileId: string) => void;
  onSaveBenchmarkValueCell: (
    cell: AthleteExerciseBenchmarkCell,
    onSuccess: () => void,
  ) => void;
  onOpenEditBenchmarkGrid: () => void;
  /** Whether the viewer holds `teams.benchmarks.edit`. */
  canEdit?: boolean;
};

export const BenchmarksDataTable = <T,>({
  data,
  coreExercises,
  benchmarkGridId,
  onSetAthleteId,
  onSaveBenchmarkValueCell,
  onOpenEditBenchmarkGrid,
  canEdit = true,
}: BenchmarksDataTableProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [focusedCellId, setFocusedCellId] = useState<string | null>(null);
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Apply filter to create filteredData
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }

    const searchTermLower = searchTerm.toLowerCase();
    return data.filter((row) => {
      const fullName = athleteDisplayName(row.athlete).toLowerCase();
      return fullName.includes(searchTermLower);
    });
  }, [data, searchTerm]);

  // Clear focused cell when exiting edit mode
  useEffect(() => {
    if (!isEditing) {
      setFocusedCellId(null);
    }
  }, [isEditing]);

  const handleEnterPress = useCallback(
    (shiftKeyPressed: boolean) => {
      const inputs = Array.from(
        document.querySelectorAll('input[type="text"][data-edit-input]'),
      ) as HTMLInputElement[];

      const currentInput = document.activeElement;
      const currentIndex = inputs.indexOf(currentInput as HTMLInputElement);

      // Move down 1 row (or up if shift is pressed)
      const nextIndex =
        currentIndex +
        (shiftKeyPressed ? -coreExercises.length : coreExercises.length);

      if (nextIndex >= 0 && nextIndex < inputs.length) {
        setFocusedIndex(nextIndex);
        // Set focus immediately in case the value didn't change
        requestAnimationFrame(() => {
          const updatedInputs = Array.from(
            document.querySelectorAll('input[type="text"][data-edit-input]'),
          ) as HTMLInputElement[];
          updatedInputs[nextIndex]?.focus();
          updatedInputs[nextIndex]?.select();
        });
      }
    },
    [coreExercises.length],
  );

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AthleteExerciseBenchmarkRow>();
    return [
      columnHelper.accessor('profile', {
        header: () => t('profile'),
        enableSorting: false,
        size: 150,
        maxSize: 150,
        cell: (info) => {
          const athlete = info.row.original.athlete;
          return (
            <div className="flex items-center gap-2">
              <AthleteAvatar
                athlete={athlete}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 12,
                  border: 3,
                  borderColor: 'white',
                  boxShadow: 1,
                }}
                imgSrc={athlete.imageUrl ?? ''}
              />
              <span>{athleteDisplayName(athlete)}</span>
            </div>
          );
        },
      }),
      ...coreExercises.map((exercise) => {
        return columnHelper.accessor(exercise.id, {
          header: () => exercise.name,
          cell: ({ row: { index, original }, column: { id }, cell, table }) => {
            const libraryExerciseId = exercise.id;
            const cellData = original[libraryExerciseId];

            if (!cellData) return null;

            const initialValue =
              cellData.value !== null && cellData.value !== undefined
                ? `${cellData.value}${cellData.unit ?? ''}`
                : '';

            const cellId = cell.id;

            const handleCellUpdate = (newValue: string) => {
              const result = table.options.meta?.updateData(
                index,
                id,
                newValue,
              );
              return result;
            };

            return (
              <EditCellInput
                key={`${cellId}-${initialValue}`}
                initialValue={initialValue}
                isEditing={isEditing}
                cellId={cellId}
                ariaLabel={t('benchmarkCellAriaLabel', {
                  exercise: exercise.name,
                  athlete: original.profile,
                })}
                onFocus={setFocusedCellId}
                onBlur={() => setFocusedCellId(null)}
                onUpdate={handleCellUpdate}
                onEnter={handleEnterPress}
              />
            );
          },
        });
      }),
    ];
  }, [coreExercises, isEditing, handleEnterPress, t]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleUpdateData = useCallback(
    (rowIndex: number, columnId: string, value: unknown) => {
      const currentRow = filteredData[rowIndex];
      if (!currentRow) return;

      const coreExerciseId = coreExercises.find(
        (exercise) => exercise.id === columnId,
      )?.id;
      if (!coreExerciseId) return;

      const result = handleValueChange(
        value as string,
        currentRow.athlete.id,
        coreExerciseId,
        benchmarkGridId,
        onSaveBenchmarkValueCell,
        toast,
        focusedIndex,
        setFocusedIndex,
        t,
      );

      // Return any error to the EditCellInput component
      return result;
    },
    [
      filteredData,
      coreExercises,
      onSaveBenchmarkValueCell,
      toast,
      focusedIndex,
      t,
      benchmarkGridId,
    ],
  );

  const handleRowClick = useCallback(
    (item: AthleteExerciseBenchmarkRow) => {
      if (!isEditing) {
        onSetAthleteId(item.id);
      }
    },
    [isEditing, onSetAthleteId],
  );

  return (
    <DataTableProvider<AthleteExerciseBenchmarkRow>
      columns={columns}
      pinColumns={{
        left: ['profile'],
      }}
      onSearchChange={handleSearchChange}
      onUpdateData={handleUpdateData}
      data={filteredData}
      isEditing={isEditing}
      onRowClick={handleRowClick}
      focusedCellId={focusedCellId}
      enablePagination
      initialPageSize={25}
      pageSizeOptions={[25, 50, 100]}
    >
      <BenchmarksDataTableCore
        setIsEditing={setIsEditing}
        onOpenEditBenchmarkGrid={onOpenEditBenchmarkGrid}
        canEdit={canEdit}
      />
    </DataTableProvider>
  );
};

export const BenchmarksDataTableCore = <T,>({
  setIsEditing,
  onOpenEditBenchmarkGrid,
  canEdit = true,
}: {
  setIsEditing: (isEditing: boolean) => void;
  onOpenEditBenchmarkGrid: () => void;
  canEdit?: boolean;
}) => {
  const {
    palette: { info, greyscale },
  } = useTheme();
  const {
    loading,
    showError,
    table,
    tableRef,
    isEditing,
    getCommonPinningStyles,
    EmptySearchResultsPlaceholder = DefaultEmptySearchResultsPlaceholder,
    ErrorPlaceholder = DefaultErrorPlaceholder,
    NoDataPlaceholder = DefaultNoDataPlaceholder,
    onSearchChange,
    searchValue: controlledSearchValue,
  } = useDataTableContext();
  const [searchValue, setSearchValue] = useState('');
  const { t } = useTranslation();

  // Use controlled search value if provided
  const currentSearchValue =
    controlledSearchValue !== undefined ? controlledSearchValue : searchValue;
  const handleSearchChange = useMemo(
    () =>
      controlledSearchValue !== undefined
        ? (value: string) => {
            onSearchChange?.(value);
          }
        : (value: string) => {
            setSearchValue(value);
            onSearchChange?.(value);
          },
    [controlledSearchValue, onSearchChange],
  );

  const hasRowData = table.getFilteredRowModel().rows.length > 0;

  const renderRow = (row: Row<T>) => {
    return <DataTableRow row={row} key={row.id} />;
  };

  let placeholderContent: ReactNode = null;

  if (!loading) {
    if (showError) {
      placeholderContent = <ErrorPlaceholder />;
    } else if (!hasRowData && !currentSearchValue) {
      placeholderContent = <NoDataPlaceholder />;
    } else if (currentSearchValue && !hasRowData) {
      placeholderContent = (
        <EmptySearchResultsPlaceholder
          clearSearch={() => {
            handleSearchChange('');
          }}
        />
      );
    }
  }

  return (
    <>
      <div
        className={cn(
          'flex items-center px-6 justify-between py-3 border-b border-gray-200',
          isEditing && 'bg-black',
        )}
      >
        <TextField
          sx={{ mt: 0, minWidth: '200px' }}
          value={currentSearchValue}
          onChange={(e) => {
            handleSearchChange(e.target.value);
          }}
          inputProps={{ 'aria-label': t('searchForProfile') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MdSearch
                  size={24}
                  className="ml-0 pl-0 text-gray-400"
                  aria-hidden="true"
                />
              </InputAdornment>
            ),
          }}
          placeholder={t('searchForProfile')}
        />
        <div className="flex gap-2">
          <DisabledWithTooltip
            disabled={!canEdit}
            reason={t('permissionDisabled.edit', { item: t('benchmarks') })}
          >
            <IconButton
              size="small"
              sx={{ gap: 0.5 }}
              onClick={() => {
                setIsEditing(!isEditing);
              }}
            >
              {isEditing ? (
                <>
                  <MdNotes size={18} className={'text-white'} />
                  <Typography
                    variant="subtitle1Semibold"
                    className={'text-white'}
                  >
                    {t('returnToViewMode')}
                  </Typography>
                </>
              ) : (
                <>
                  <MdEditNote size={18} />
                  <Typography variant="subtitle1Semibold">
                    {t('editMode')}
                  </Typography>
                </>
              )}
            </IconButton>
          </DisabledWithTooltip>
          <DisabledWithTooltip
            disabled={!canEdit}
            reason={t('permissionDisabled.edit', { item: t('benchmarks') })}
          >
            <IconButton onClick={onOpenEditBenchmarkGrid}>
              <MdSettings
                size={16}
                className={isEditing ? 'text-white' : 'text-black'}
              />
              <Typography
                variant="subtitle1Semibold"
                className={cn('ml-1', isEditing && 'text-white')}
              >
                {t('settings')}
              </Typography>
            </IconButton>
          </DisabledWithTooltip>
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-info-25 z-10 overflow-hidden">
          <CircularProgress
            variant="indeterminate"
            sx={{ color: info.main }}
            size={'3rem'}
          />
        </div>
      )}
      <TableContainer
        tabIndex={0}
        role="region"
        aria-label={t('benchmarks')}
        sx={{
          flexGrow: 1,
        }}
      >
        {placeholderContent || (
          <Table
            ref={tableRef}
            stickyHeader
            sx={{ '& th': { backgroundColor: greyscale[100] } }}
          >
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableCell
                        className={cn(
                          'whitespace-nowrap transition-[padding] delay-200 pl-6',
                        )}
                        key={header.id}
                        id={`header-${header.id}`}
                        style={{
                          width: header.getSize(),
                          ...getCommonPinningStyles(header.column, true),
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div
                          className={cn(
                            'sticky flex flex-nowrap items-center gap-2',
                          )}
                        >
                          <div className="overflow-hidden overflow-ellipsis">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getPaginationRowModel().rows.map(renderRow)}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <PaginationFooter />
    </>
  );
};
